import React from 'react';
import { type MenuItemComponentType } from './types';
import MenuItem from './MenuItem';
import classnames from 'classnames';
import styles from './Menu.module.css';

interface MenuProps<MenuItemObject, SearchResult> {
  menuItemsLoading: boolean;
  observer: IntersectionObserver;
  menuItems: SearchResult[];
  MenuItemComponent: MenuItemComponentType<MenuItemObject>;
  menuItemUniqueKey: keyof MenuItemObject;
  selectedIndex: number;
  onSelectItem: (index: number, event: KeyboardEvent | MouseEvent) => void;
  onMouseOverIndex(index: number): void;
  onMouseMove(): void;
  classNames?: {
    menu?: string;
    menuItem?: string;
  };
  searchingPlaceholder: string;
}

const Menu = <MenuItemObject, SearchResult>({
  menuItemsLoading,
  observer,
  MenuItemComponent,
  menuItemUniqueKey,
  menuItems,
  onMouseMove,
  onMouseOverIndex,
  onSelectItem,
  selectedIndex,
  classNames,
  searchingPlaceholder,
}: MenuProps<MenuItemObject, SearchResult>) => {
  const isHidden = !menuItemsLoading && menuItems.length === 0;
  let index = -1;

  const renderElement = (item: MenuItemObject, i: number) => (
    <MenuItem
      item={item}
      index={i}
      onSelectItem={onSelectItem}
      onMouseOverIndex={onMouseOverIndex}
      selected={i === selectedIndex}
      observer={observer}
      MenuItemComponent={MenuItemComponent}
      className={classNames?.menuItem}
    />
  );

  const selectedItemStyle = {
    backgroundColor: 'var(--theme-menu-hover-background)',
  };

  const itemsList = menuItems.map((item) => {
    if (Array.isArray(item)) {
      return (
        <aha-menu-group key={`item-${index}`}>
          {item.map((subItem: MenuItemObject, subItemIndex) => {
            index++;
            return (
              <aha-menu-item
                style={selectedIndex === index ? selectedItemStyle : {}}
                key={subItem[menuItemUniqueKey] as string}
              >
                {renderElement(subItem, index)}
                {subItemIndex === item.length - 1 && (
                  <div className={styles.divider} key={`divider-${index}`} />
                )}
              </aha-menu-item>
            );
          })}
        </aha-menu-group>
      );
    } else {
      index++;
      return (
        <aha-menu-item
          style={selectedIndex === index ? selectedItemStyle : {}}
          key={(item as unknown as MenuItemObject)[menuItemUniqueKey] as string}
        >
          {renderElement(item as unknown as MenuItemObject, index)}
        </aha-menu-item>
      );
    }
  });

  return (
    <div
      className={classnames(styles.menu, classNames?.menu, {
        [styles.hidden]: isHidden,
      })}
      role="list"
      onMouseMove={onMouseMove}
      contentEditable={false}
    >
      <div className={styles.menuContent} data-testid="menu-decorator">
        {menuItemsLoading && (
          <aha-menu-item disabled>
            <span className={styles.searchingText}>
              <aha-spinner size="16px" /> {searchingPlaceholder}
            </span>
          </aha-menu-item>
        )}
        {!menuItemsLoading && itemsList}
      </div>
    </div>
  );
};

export default Menu;
